import { useState, MouseEvent } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Button, Card, CardContent, IconButton, Menu, MenuItem, Typography } from "@mui/material"
import { TableChartOutlined } from "@mui/icons-material"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import Confirm from "components/confirm-dialog"
import { useIsOpPro } from "resources/account"
import { Creators as ApiWrapperCreators } from "model/apiWrapper"
import PermissionButton from "modules/resources/components/resource-menu/permission-button"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as NotificationCreators } from "modules/notifications/model"
import EditSigmaSolution from "connected-components/solutions/components/edit-sigma-solution"
import type { SigmaSolution } from "connected-components/solutions/types"

import "./style.scss"

interface SigmaSolutionCardProps {
  solution: SigmaSolution
  isSuperuser: boolean
  accountAlias: string
  onRefresh: () => void
}

function SigmaSolutionCard({ solution, isSuperuser, accountAlias, onRefresh }: SigmaSolutionCardProps) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const isOpPro = useIsOpPro()

  const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setMenuAnchorEl(null)
  }

  const handleView = () => {
    const path = `/${accountAlias}/solutions/${solution.id}`
    navigate(path)
  }

  const handleEdit = () => {
    handleCloseMenu()
    dispatch(DialogCreators.open({
      title: "Edit Solution",
      content: (
        <EditSigmaSolution
          key={solution.id}
          solution={solution}
          onSuccess={onRefresh}
        />
      ),
    }))
  }

  const handleDelete = () => {
    handleCloseMenu()
    dispatch(DialogCreators.open({
      title: `Delete Solution: ${solution.name}`,
      content: (
        <Confirm
          text={`Are you sure you want to delete ${solution.name}? This action cannot be undone.`}
          keyword={solution.name}
          cta="Delete Solution"
          action={() => {
            dispatch(ApiWrapperCreators.delete(`/api/${accountAlias}/solutions/sigma/${solution.id}`,
              {},
              () => {
                dispatch(NotificationCreators.add("info", null, "Solution deleted successfully"))
                onRefresh()
                dispatch(DialogCreators.close())
              },
              () => {
                dispatch(NotificationCreators.add("error", null, "An error occurred while deleting the solution"))
                dispatch(DialogCreators.close())
              },
            ))
          }}
          onClose={() => dispatch(DialogCreators.close())}
        />
      ),
    }))
  }

  const resource = {
    alias: solution.alias,
    shortType: "solution",
    resourceType: "solution",
  }

  return (
    <Card square className="sigma-solution-card">
      <div className="sigma-card-top">
        <div className="sigma-card-icon">
          <TableChartOutlined fontSize="large" />
        </div>
        <div className="sigma-card-info">
          <Typography variant="h5" component="h5" className="sigma-card-title">
            {solution.name}
          </Typography>
        </div>
        <div>
          <PermissionButton {...{ account: accountAlias, resource, isOpPro }} />
          {isSuperuser && (
            <IconButton
              className="sigma-card-menu-button"
              onClick={handleOpenMenu}
              size="large"
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </div>
      </div>
      <CardContent className="sigma-card-body">
        <Typography variant="body1" color="textSecondary" className="sigma-card-description">
          {solution.description || ""}
        </Typography>
        <div className="sigma-card-footer">
          <Button color="primary" variant="contained" onClick={handleView}>
            View Dashboard
          </Button>
        </div>
      </CardContent>
      {isSuperuser && (
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={handleEdit}>Edit</MenuItem>
          <MenuItem onClick={handleDelete}>Delete</MenuItem>
        </Menu>
      )}
    </Card>
  )
}

export default SigmaSolutionCard
